import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, Observable, Subscription } from 'rxjs';
import * as LD from 'launchdarkly-js-client-sdk';
import { environment } from 'src/environments/environment';
import { currentUser } from '../../auth';
import { select, Store } from '@ngrx/store';
import { AppState } from '../../reducers';
import { filter } from 'rxjs/operators';

export enum FeatureFlag {
  ENABLE_TIMELINE_TASK_CACHING = 'enable_timeline_task_caching',
  ENABLE_ASYNC_TASK_DETAILS_LOADING = 'enable_async_task_details_loading',
}

@Injectable({
  providedIn: 'root',
})
export class FeatureFlagService implements OnDestroy {
  private ldClient: LD.LDClient | null = null;

  private currentUserSub: Subscription | null = null;

  // Store flag values and updates using BehaviorSubjects
  private flagSubjects: Map<FeatureFlag, BehaviorSubject<boolean | null>> = new Map(); // Allow `null` for loading state

  constructor(private store: Store<AppState>) {}

  /**
   * Initializes LaunchDarkly client with the current user
   */
  public initializeClient(): Promise<void> {
    return new Promise((resolve, reject) => {
      this.currentUserSub = this.store
        .pipe(
          select(currentUser),
          filter((res) => !!res.id),
        )
        .subscribe((user) => {
          if (user) {
            let context;

            if (user.companyID) {
              const companyContext = {
                kind: 'company',
                key: user.companyUUID,
                name: user.companyName,
              };

              const userContext = {
                kind: 'user',
                key: user.id.toString(),
                firstName: user.firstName,
                lastName: user.lastName,
                email: user.email,
                companyId: user.companyUUID,
                companyName: user.companyName,
              };

              const multiContext = {
                kind: 'multi',
                user: userContext,
                company: companyContext,
              };

              context = multiContext;
            } else {
              const userContext = {
                kind: 'user',
                key: user.id.toString(),
                firstName: user.firstName,
                lastName: user.lastName,
                email: user.email,
              };

              context = userContext;
            }

            const options: LD.LDOptions = {
              logger: LD.basicLogger({ level: 'warn' }),
            };

            // TODO: Temporarily disabled relay proxy due to CORS issues
            // Re-enable once infrastructure CORS fix is deployed
            // Configure relay proxy if URL is provided
            // if ((environment as any).LAUNCHDARKLY_RELAY_URL) {
            //   options.baseUrl = (environment as any).LAUNCHDARKLY_RELAY_URL;
            //   options.streamUrl = (environment as any).LAUNCHDARKLY_RELAY_URL;
            //   options.eventsUrl = (environment as any).LAUNCHDARKLY_RELAY_URL;
            // }

            this.ldClient = LD.initialize(environment.LUNCHDARKLY_SDK_KEY, context, options);

            this.ldClient.on('ready', () => {
              this.loadAllFlags(); // Load all flags once client is ready
              resolve();
            });

            this.ldClient.on('error', (err) => {
              reject(err);
            });

            this.ldClient.on('change', (changes) => this.handleFlagChanges(changes));
          }
        });
    });
  }

  /**
   * Load all flags from LaunchDarkly and update the BehaviorSubjects
   */
  private loadAllFlags() {
    if (this.ldClient) {
      const allFlags = this.ldClient.allFlags();

      Object.entries(allFlags).forEach(([flagKey, flagValue]) => {
        const flagSubject = this.flagSubjects.get(flagKey as FeatureFlag);

        if (flagSubject) {
          // Update the existing subject with the new flag value
          flagSubject.next(flagValue as boolean);
        } else {
          // Create a new subject if it doesn't exist yet and initialize it with the flag value
          this.flagSubjects.set(
            flagKey as FeatureFlag,
            new BehaviorSubject<boolean | null>(flagValue as boolean),
          );
        }
      });
    }
  }

  /**
   * Get the current value of a feature flag and listen to changes
   * @param flagKey FeatureFlag enum key
   */
  public getFlagValue(flagKey: FeatureFlag): Observable<boolean | null> {
    let subject = this.flagSubjects.get(flagKey);

    if (!subject) {
      // Create a new subject with default value `null` if the flag isn't ready yet
      subject = new BehaviorSubject<boolean | null>(null);
      this.flagSubjects.set(flagKey, subject);
    }

    return subject.asObservable(); // Return the observable for consumers
  }

  /**
   * Handle flag changes from LaunchDarkly and update BehaviorSubjects
   */
  private handleFlagChanges(changes: Record<string, any>) {
    Object.entries(changes).forEach(([flagKey, change]) => {
      const newValue = change.current as boolean;
      const subject = this.flagSubjects.get(flagKey as FeatureFlag);
      if (subject) {
        subject.next(newValue); // Emit new value to subscribers
      }
    });
  }

  /**
   * Clean up when service is destroyed to avoid memory leaks
   */
  ngOnDestroy() {
    if (this.ldClient) {
      this.ldClient.off('change', this.handleFlagChanges.bind(this));
      this.ldClient.close();
    }
    this.currentUserSub?.unsubscribe();
  }
}
